import { Check, Clear } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  List,
  ListItem,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { DateTime } from 'luxon';
import { type FC, useState } from 'react';
import type { GlassWasherData, IIsPdf } from '../../models';
import { AvaniLightTheme } from '../../theme/schemes/AvaniLightTheme';
import { LightboxGallery } from '../Dialogs';
import { AvaniScrollable } from '../Utils';

const AvaniStyledCard = styled(Card)(
  () => `
  border-radius: 16px;
  padding: 16px;

  .MuiTypography-root.MuiTypography-h3 {
    margin: 0;
    line-height: 1;
    padding-bottom: 12px;
  }
`
);

export const AvaniGlassWasherCard: FC<GlassWasherData & IIsPdf> = ({
  glassWasherName,
  detergentNew,
  detergentCurrent,
  detergentConsumption,
  rinseCurrent,
  rinseNew,
  media,
  rinseConsumption,
  hygieneStatusVisitDatesTrend,
  isPDF = false
}) => {
  const theme = isPDF ? AvaniLightTheme : useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const readableDate = (dateObject: Date) => {
    return DateTime.fromISO(new Date(dateObject).toISOString()).toFormat('dd/MM');
  };
  // const [toggle, setToggle] = useState(false);
  // const handleClose = () => {
  //   setToggle(false);
  // };
  const failedHygiene = hygieneStatusVisitDatesTrend.some(({ hygieneStatus }) => hygieneStatus === false);

  return (
    <AvaniStyledCard
      raised={true}
      sx={{
        height: '100%',
        textAlign: 'center',
        backgroundColor: failedHygiene ? theme.colors.avani.lightMagenta : theme.colors.avani.lightTurc
      }}
    >
      <Tooltip title={glassWasherName} arrow>
        <Typography variant="h3" color={theme.colors.avani.teal} noWrap>
          {glassWasherName}
        </Typography>
      </Tooltip>
      <Typography variant="h4" color={theme.colors.avani.teal} sx={{ py: '12px', textAlign: 'left' }}>
        Trend
      </Typography>
      {!!hygieneStatusVisitDatesTrend.length && (
        <AvaniScrollable height={isMobile ? 78 : 92} horizontal>
          <List sx={{ width: 'max-content', float: 'left', display: 'block' }}>
            {hygieneStatusVisitDatesTrend.map((item, ind) => {
              const [toggle, setToggle] = useState(false);
              const handleClose = () => {
                setToggle(false);
              };

              const trendMedia = ind ? item.media : media;

              const hasItemMedia = !!trendMedia?.length;
              return (
                <ListItem
                  disablePadding
                  key={ind}
                  onClick={() => {
                    if (!hasItemMedia) return;
                    setToggle(true);
                  }}
                  sx={{
                    cursor: hasItemMedia ? 'pointer' : '',
                    display: 'block',
                    width: 'auto',
                    minWidth: '80px',
                    float: 'left',
                    padding: '0 12px 0 0'
                  }}
                >
                  <Avatar
                    sx={{
                      margin: '0 auto',
                      backgroundColor: !item.hygieneStatus ? 'rgba(230,0,126, 0.4)' : 'rgba(0,167,168, 0.4)',
                      border: hasItemMedia ? `2px solid ${theme.colors.avani.teal}` : ''
                    }}
                  >
                    {item.hygieneStatus ? (
                      <Check sx={{ fill: theme.colors.avani.turc }} />
                    ) : (
                      <Clear sx={{ fill: theme.colors.avani.magenta }} />
                    )}
                  </Avatar>
                  <Typography
                    variant={'body2'}
                    color={!item.hygieneStatus ? theme.colors.avani.magenta : theme.colors.avani.turc}
                    sx={{ width: '100%', textAlign: 'center' }}
                  >
                    {readableDate(item.date)}
                  </Typography>
                  {hasItemMedia && (
                    <LightboxGallery
                      open={toggle}
                      index={0}
                      handleClose={handleClose}
                      imageSrc={trendMedia}
                      theme={theme}
                    />
                  )}
                </ListItem>
              );
            })}
          </List>
        </AvaniScrollable>
      )}
      <Box sx={{ display: 'flex' }}>
        <Typography
          variant="h4"
          color={theme.colors.avani.teal}
          sx={{
            display: 'inline-block',
            py: '12px',
            pt: '24px',
            width: '100%',
            textAlign: 'left',
            flexGrow: 1
          }}
        >
          Chemical consumption
        </Typography>
      </Box>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'transparent' }}>
            <TableCell></TableCell>
            <TableCell align="center" sx={{ textTransform: 'none', color: theme.colors.avani.teal }}>
              Detergent
            </TableCell>
            <TableCell align="center" sx={{ textTransform: 'none', color: theme.colors.avani.teal }}>
              Rinse
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ textTransform: 'none', color: theme.colors.avani.teal }}>Remaining</TableCell>
            <TableCell sx={{ textTransform: 'none', color: theme.colors.avani.teal }} align="center">
              {detergentCurrent ?? 0} L
            </TableCell>
            <TableCell sx={{ textTransform: 'none', color: theme.colors.avani.teal }} align="center">
              {rinseCurrent ?? 0} L
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ textTransform: 'none', color: theme.colors.avani.teal }}>Added</TableCell>
            <TableCell sx={{ textTransform: 'none', color: theme.colors.avani.teal }} align="center">
              {detergentNew ?? 0} L
            </TableCell>
            <TableCell sx={{ textTransform: 'none', color: theme.colors.avani.teal }} align="center">
              {rinseNew ?? 0} L
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ textTransform: 'none', color: theme.colors.avani.teal }}>Consumption</TableCell>
            <TableCell sx={{ textTransform: 'none', color: theme.colors.avani.teal }} align="center">
              {detergentConsumption ?? 0} L
            </TableCell>
            <TableCell sx={{ textTransform: 'none', color: theme.colors.avani.teal }} align="center">
              {rinseConsumption ?? 0} L
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </AvaniStyledCard>
  );
};
